"use client"

import { useState, useEffect, ChangeEvent } from "react"
import axios from "axios"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import { Checkbox } from "../ui/checkbox"
import { Eye, Hourglass, Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { TimesheetReviewModal } from "../modals/timesheet-review-modal"
import { LeaveReviewModal } from "../modals/leave-review-modal"
import { BulkApprovalModal } from "../modals/bulk-approval-modal"
import { Pagination } from "../common/pagination-dynamic"
import { CommentsModal } from "../modals/comments-modal"
import { toast } from "../hooks/use-toast"

// Interfaces for API data
interface TimesheetEntry {
  id: number
  name: string
  duration: string
  time: string
  submittedOn: string
  comments: number
  commentsData: CommentData[]
  tsstatus: "pending" | "approved" | "rejected"
}

interface CommentData {
  id: number
  user: { name: string; initials: string }
  action: string
  timestamp: string
  commentText: string
}

interface ApiTimesheetData {
  timesheetID: number
  userID: number
  employeeName?: string
  submittedBy?: string
  startDate: string
  endDate: string
  status: number
  hours?: number
  minutes?: number
  hoursTotal?: number
  minutesTotal?: number
  submittedOn: string
  submissionComment?: string
  approvedOn?: string
  approvalComment?: string
  approvedBy?: string
  displayTitle: string
  commentsCount: number
  rejectedBy?: string
  rejectionComment?: string
  rejectedOn?: string
}

// removed static mock timesheet data

// Dynamic Leave Approvals (Manager)
interface LeaveDayDetail {
  leaveDate: string
  dayType: string
}

interface ApiLeaveItem {
  status: number
  modUser: number
  requestID: number
  userID: number
  requestedBy: string
  leaveTypeID: number
  leaveName: string
  requestDate: string
  leaveStartDate: string
  leaveEndDate: string
  totalDays: number
  reason: string
  comments: string
  createdBy: string
  createdOn: string
  modifiedBy: string
  modifiedOn: string
  managerResponseOn?: string
  managerName?: string
  leaveDayDetails?: LeaveDayDetail[]
}

interface LeaveEntry {
  id: number
  requestID: number
  requestBy: string
  leaveType: string
  fromDate: string
  toDate: string
  totalDays: number
  appliedOn: string
  reason: string
  comments?: string
  rawStatus: number
  statusLabel: "pending" | "approved" | "rejected" | "cancelled"
  managerResponseOn?: string
  modifiedOn?: string
  managerName?: string
  leaveDayDetails?: LeaveDayDetail[]
}


export function PendingApprovalPage() {
  const [isTimesheetModalOpen, setIsTimesheetModalOpen] = useState(false)
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false)
  const [isBulkApprovalModalOpen, setIsBulkApprovalModalOpen] = useState(false)
  const [selectedTimesheet, setSelectedTimesheet] = useState(null)
  const [selectedLeave, setSelectedLeave] = useState(null)
  const [selectedTimesheets, setSelectedTimesheets] = useState<number[]>([])
  const [timesheetPageSize, setTimesheetPageSize] = useState(10)
  const [timesheetCurrentPage, setTimesheetCurrentPage] = useState(1)
  const [leavePageSize, setLeavePageSize] = useState(10)
  const [leaveCurrentPage, setLeaveCurrentPage] = useState(1)
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false)
  const [selectedComments, setSelectedComments] = useState<any[]>([])
  const [selectedCommentsTitle, setSelectedCommentsTitle] = useState("")
  const [timesheetSearchTerm, setTimesheetSearchTerm] = useState("")
  const [leaveSearchTerm, setLeaveSearchTerm] = useState("")
  const [sortField, setSortField] = useState<"name" | "duration" | "submittedOn" | null>(null)
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const [leaveSortField, setLeaveSortField] = useState<"requestBy" | "leaveType" | "fromDate" | "toDate" | "totalDays" | null>(null)
  const [leaveSortDirection, setLeaveSortDirection] = useState<"asc" | "desc">("asc")

  // API state management
  const [apiTimesheetData, setApiTimesheetData] = useState<TimesheetEntry[]>([])
  const [timesheetLoading, setTimesheetLoading] = useState(true)
  const [timesheetError, setTimesheetError] = useState<string | null>(null)

  const [apiLeaveData, setApiLeaveData] = useState<LeaveEntry[]>([])
  const [leaveLoading, setLeaveLoading] = useState(true)
  const [leaveError, setLeaveError] = useState<string | null>(null)

  // API functions
  const fetchPendingTimesheets = async () => {
    setTimesheetLoading(true)
    setTimesheetError(null)
    try {
      const [pendingRes, approvedRes] = await Promise.all([
        axios.get(process.env.REACT_APP_API_URL +"/api/timesheet/GetPendingTimesheets", { withCredentials: true }),
        axios.get(process.env.REACT_APP_API_URL +"/api/timesheet/approved", { withCredentials: true })
      ])

      // Extract arrays
      const pendingRaw = Array.isArray(pendingRes.data?.data) ? pendingRes.data.data : (Array.isArray(pendingRes.data) ? pendingRes.data : [])
      const approvedRaw = Array.isArray(approvedRes.data?.data) ? approvedRes.data.data : (Array.isArray(approvedRes.data) ? approvedRes.data : [])

      const formatSubmitted = (iso?: string): string => {
        if (!iso) return ""
        const d = new Date(iso)
        if (isNaN(d.getTime())) return ""
        const day = String(d.getDate()).padStart(2, '0')
        const month = d.toLocaleString('en-GB', { month: 'short' })
        const year = d.getFullYear()
        const hours = String(d.getHours()).padStart(2, '0')
        const minutes = String(d.getMinutes()).padStart(2, '0')
        return `${day}-${month}-${year} ${hours}:${minutes}`
      }

      const normalize = (item: ApiTimesheetData, forcedStatus?: "pending" | "approved" | "rejected"): TimesheetEntry => {
        let statusString: "pending" | "approved" | "rejected" = forcedStatus || "pending"
        if (!forcedStatus) {
          if (item.status === 2) statusString = "approved"
          else if (item.status === 3) statusString = "rejected"
        }

        const displayName = item.employeeName || item.submittedBy || "Unknown User"
        const hoursValue = typeof item.hours === 'number' ? item.hours : (item.hoursTotal ?? 0)
        const minutesValue = typeof item.minutes === 'number' ? item.minutes : (item.minutesTotal ?? 0)

        return {
          id: item.timesheetID,
          name: displayName,
          duration: item.displayTitle || `${new Date(item.startDate).toLocaleDateString()} - ${new Date(item.endDate).toLocaleDateString()}`,
          time: `${hoursValue}h ${minutesValue}m`,
          submittedOn: formatSubmitted(item.submittedOn),
          comments: item.commentsCount ?? 0,
          commentsData: [],
          tsstatus: statusString
        }
      }

      const pending = pendingRaw.map((item: ApiTimesheetData) => normalize(item, "pending"))
      const approved = approvedRaw.map((item: ApiTimesheetData) => normalize(item, "approved"))

      const merged = [...pending, ...approved]
      setApiTimesheetData(merged)
    } catch (error: any) {
      setTimesheetError("Failed to load pending timesheets")
      setApiTimesheetData([])
    } finally {
      setTimesheetLoading(false)
    }
  }

  const fetchManagerApprovals = async () => {
    setLeaveLoading(true)
    setLeaveError(null)
    try {
      // API is POST; backend reads modUser from session, so empty body
      const res = await axios.post(process.env.REACT_APP_API_URL +"/api/Leave/manager-approvals", {}, { withCredentials: true })
      // Handle different response structures
      let raw: any[] = []
      if (Array.isArray(res.data?.data)) {
        raw = res.data.data
      } else if (Array.isArray(res.data)) {
        raw = res.data
      } else if (res.data?.data && typeof res.data.data === 'object') {
        // Handle case where data might be an object with an array property
        raw = []
      }
      
  
      const formatDate = (iso?: string): string => {
        if (!iso) return ""
        const d = new Date(iso)
        if (isNaN(d.getTime())) return ""
        const day = String(d.getDate()).padStart(2, '0')
        const month = d.toLocaleString('en-GB', { month: 'short' })
        const year = d.getFullYear()
        return `${day}-${month}-${year}`
      }

      // Status mapping per backend:
      // 5 = Pending Manager Approval
      // 6 = Pending HR Approval (Manager Approved)
      // 7 = Rejected by Manager
      // 9 = Cancelled
      const isPendingStatus = (code: number) => code === 5
      const isApprovedStatus = (code: number) => code === 6
      const isRejectedStatus = (code: number) => code === 7
      const isCancelledStatus = (code: number) => code === 9

      const mapStatusToLabel = (status: unknown): "pending" | "approved" | "rejected" | "cancelled" => {
        if (typeof status === "number") {
          if (isPendingStatus(status)) return "pending"
          if (isApprovedStatus(status)) return "approved"
          if (isRejectedStatus(status)) return "rejected"
          if (isCancelledStatus(status)) return "cancelled"
          return "pending"
        }
        if (typeof status === "string") {
          const s = status.toLowerCase()
          if (s.includes("reject")) return "rejected"
          if (s.includes("approve")) return "approved"
          if (s.includes("cancel")) return "cancelled"
          return "pending"
        }
        return "pending"
      }

      const normalized: LeaveEntry[] = raw
        .filter((item) => {
          const status = typeof item.status === "number" ? item.status : undefined
          return status === 5 || status === 7
        })
        .map((item: any) => {
          const statusLabel: "pending" | "approved" | "rejected" | "cancelled" = mapStatusToLabel(item.status as unknown)
          
          
          // Extract managerName directly from API response - it's guaranteed to be there
          const managerName = item.managerName
          
          const mapped: LeaveEntry = {
            id: item.requestID,
            requestID: item.requestID,
            requestBy: item.requestedBy,
            leaveType: item.leaveName,
            fromDate: formatDate(item.leaveStartDate),
            toDate: formatDate(item.leaveEndDate),
            totalDays: item.totalDays,
            appliedOn: formatDate(item.requestDate),
            reason: item.reason,
            comments: item.comments,
            rawStatus: item.status as unknown as number,
            statusLabel,
            managerResponseOn: item.managerResponseOn,
            modifiedOn: item.modifiedOn,
            managerName: item.managerName, // CRITICAL: Include managerName from API
            leaveDayDetails: item.leaveDayDetails || [],
          }

          return mapped
        })

      setApiLeaveData(normalized)
    } catch (error: any) {
      setLeaveError("Failed to load leave approvals")
      setApiLeaveData([])
    } finally {
      setLeaveLoading(false)
    }
  }

  const fetchTimesheetComments = async (timesheetId: number) => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL + `/api/timesheet/gettimesheetcomments?timesheetId=${timesheetId}`, { withCredentials: true })
      return response.data ?? []
    } catch (error: any) {
      return []
    }
  }

  // Fetch data on component mount
  useEffect(() => {
    fetchPendingTimesheets()
    fetchManagerApprovals()
    
    // Auto-refresh leave approvals every 30 seconds
    const interval = setInterval(() => {
      fetchManagerApprovals()
    }, 30000)
    
    return () => clearInterval(interval)
  }, [])

  // Reset to first page when search terms change
  useEffect(() => {
    setTimesheetCurrentPage(1)
  }, [timesheetSearchTerm])

  useEffect(() => {
    setLeaveCurrentPage(1)
  }, [leaveSearchTerm])

  const handleTimesheetReview = (timesheet: any) => {
    setSelectedTimesheet(timesheet)
    setIsTimesheetModalOpen(true)
  }

  const handleLeaveReview = (leave: any) => {
    setSelectedLeave(leave)
    setIsLeaveModalOpen(true)
  }

  const notify = (title: string, description?: string) => {
    try {
      toast({ title, description })
    } catch (_) {
      // noop if toast not available in this context
    }
  }

  const handleViewComments = async (timesheet: any) => {
    try {
      const comments = await fetchTimesheetComments(timesheet.id)

      // Transform API comments (commentTypeText/commentByUser/commentDate/commentText) to CommentsModal format
      const toRelative = (dateIso?: string) => {
        if (!dateIso) return "Unknown time"
        const date = new Date(dateIso)
        const now = new Date()
        const diffMs = Math.max(0, now.getTime() - date.getTime())
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
        const months = Math.floor(diffDays / 30)
        const label = months >= 1 ? `${months} month${months > 1 ? 's' : ''} ago` : `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
        const detailed = date.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        return `${label} (${detailed})`
      }

      const transformedComments = Array.isArray(comments) ? comments.map((comment: any) => {
        const displayName = comment.commentByUser || comment.userName || comment.commentedBy || "Unknown User"
        const actionWord = (() => {
          const t = (comment.commentTypeText || '').toLowerCase()
          if (t.includes('approval')) return 'approved the Timesheet'
          if (t.includes('rejection') || t.includes('reject')) return 'rejected the Timesheet'
          if (t.includes('submission') || t.includes('submit')) return 'submitted the Timesheet'
          return 'commented'
        })()
        const when = toRelative(comment.commentDate || comment.timestamp || comment.createdDate)
        return {
          id: comment.id || Math.random(),
          user: {
            name: displayName,
            initials: (displayName || "UU").split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2),
            avatarUrl: undefined
          },
          action: actionWord,
          timestamp: when,
          commentText: comment.commentText || comment.comment || ""
        }
      }) : []

      setSelectedComments(transformedComments)
      setSelectedCommentsTitle(`${timesheet.name}'s Timesheet`)
      setIsCommentsModalOpen(true)
    } catch (error) {
      setSelectedComments([])
      setSelectedCommentsTitle(`${timesheet.name}'s Timesheet`)
      setIsCommentsModalOpen(true)
    }
  }

  const handleTimesheetSelect = (timesheetId: number, checked: boolean) => {
    if (checked) {
      setSelectedTimesheets((prev) => [...prev, timesheetId])
    } else {
      setSelectedTimesheets((prev) => prev.filter((id) => id !== timesheetId))
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const pendingTimesheets = filteredTimesheetData.filter((ts) => ts.tsstatus === "pending").map((ts) => ts.id)
      setSelectedTimesheets(pendingTimesheets)
    } else {
      setSelectedTimesheets([])
    }
  }

  const handleBulkApproval = () => {
    // Disabled for now
  }

  const handleBulkApprove = async (_comment: string) => {
    // Disabled: no API call
    setSelectedTimesheets([])
  }

  const handleBulkReject = async (_comment: string) => {
    // Disabled: no API call
    setSelectedTimesheets([])
  }

  // Sorting function
  const handleSort = (field: "name" | "duration" | "submittedOn") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
    setTimesheetCurrentPage(1) // Reset to first page when sorting
  }

  // Get sort icon
  const getSortIcon = (field: "name" | "duration" | "submittedOn") => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4" />
    return sortDirection === "asc" ? 
      <ArrowUp className="h-4 w-4" /> : 
      <ArrowDown className="h-4 w-4" />
  }

  // Filter timesheet data based on search term
  let filteredTimesheetData = apiTimesheetData.filter((timesheet) => {
    if (!timesheetSearchTerm.trim()) return true
    const term = timesheetSearchTerm.toLowerCase()
    return (
      timesheet.name?.toLowerCase().includes(term) ||
      timesheet.duration?.toLowerCase().includes(term) ||
      timesheet.time?.toLowerCase().includes(term) ||
      timesheet.submittedOn?.toLowerCase().includes(term)
    )
  })

  // Apply sorting to filtered data
  if (sortField) {
    filteredTimesheetData = [...filteredTimesheetData].sort((a, b) => {
      let aValue: any, bValue: any
      
      switch (sortField) {
        case "name":
          aValue = a.name?.toLowerCase() || ""
          bValue = b.name?.toLowerCase() || ""
          break
        case "duration":
          aValue = a.duration?.toLowerCase() || ""
          bValue = b.duration?.toLowerCase() || ""
          break
        case "submittedOn":
          // Parse date strings for proper sorting (format: "DD-MMM-YYYY HH:MM")
          const parseDate = (dateStr: string) => {
            if (!dateStr) return 0
            // Try to parse the date string
            try {
              // Format: "18-Aug-2024 14:30" -> "Aug 18, 2024 14:30"
              const parts = dateStr.split(" ")
              if (parts.length >= 2) {
                const datePart = parts[0] // "18-Aug-2024"
                const timePart = parts[1] // "14:30"
                const [day, month, year] = datePart.split("-")
                const monthMap: { [key: string]: string } = {
                  "Jan": "01", "Feb": "02", "Mar": "03", "Apr": "04",
                  "May": "05", "Jun": "06", "Jul": "07", "Aug": "08",
                  "Sep": "09", "Oct": "10", "Nov": "11", "Dec": "12"
                }
                const monthNum = monthMap[month] || "01"
                const isoDate = `${year}-${monthNum}-${day.padStart(2, "0")}T${timePart}:00`
                return new Date(isoDate).getTime()
              }
              return new Date(dateStr).getTime()
            } catch {
              return 0
            }
          }
          aValue = parseDate(a.submittedOn || "")
          bValue = parseDate(b.submittedOn || "")
          break
        default:
          return 0
      }
      
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue
      }
      
      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })
  }

  // Leave sorting function
  const handleLeaveSort = (field: "requestBy" | "leaveType" | "fromDate" | "toDate" | "totalDays") => {
    if (leaveSortField === field) {
      setLeaveSortDirection(leaveSortDirection === "asc" ? "desc" : "asc")
    } else {
      setLeaveSortField(field)
      setLeaveSortDirection("asc")
    }
    setLeaveCurrentPage(1) // Reset to first page when sorting
  }

  // Get leave sort icon
  const getLeaveSortIcon = (field: "requestBy" | "leaveType" | "fromDate" | "toDate" | "totalDays") => {
    if (leaveSortField !== field) return <ArrowUpDown className="h-4 w-4" />
    return leaveSortDirection === "asc" ? 
      <ArrowUp className="h-4 w-4" /> : 
      <ArrowDown className="h-4 w-4" />
  }

  // Filter leave data based on search term
  let filteredLeaveData = apiLeaveData.filter((leave) => {
    if (!leaveSearchTerm.trim()) return true
    const term = leaveSearchTerm.toLowerCase()
    return (
      leave.requestBy?.toLowerCase().includes(term) ||
      leave.leaveType?.toLowerCase().includes(term) ||
      leave.fromDate?.toLowerCase().includes(term) ||
      leave.toDate?.toLowerCase().includes(term)
    )
  })

  // Apply sorting to filtered leave data
  if (leaveSortField) {
    filteredLeaveData = [...filteredLeaveData].sort((a, b) => {
      let aValue: any, bValue: any
      
      switch (leaveSortField) {
        case "requestBy":
          aValue = a.requestBy?.toLowerCase() || ""
          bValue = b.requestBy?.toLowerCase() || ""
          break
        case "leaveType":
          aValue = a.leaveType?.toLowerCase() || ""
          bValue = b.leaveType?.toLowerCase() || ""
          break
        case "fromDate":
        case "toDate":
          // Parse date strings for proper sorting (format: "DD-MMM-YYYY")
          const parseLeaveDate = (dateStr: string) => {
            if (!dateStr) return 0
            try {
              const [day, month, year] = dateStr.split("-")
              const monthMap: { [key: string]: string } = {
                "Jan": "01", "Feb": "02", "Mar": "03", "Apr": "04",
                "May": "05", "Jun": "06", "Jul": "07", "Aug": "08",
                "Sep": "09", "Oct": "10", "Nov": "11", "Dec": "12"
              }
              const monthNum = monthMap[month] || "01"
              const isoDate = `${year}-${monthNum}-${day.padStart(2, "0")}`
              return new Date(isoDate).getTime()
            } catch {
              return 0
            }
          }
          aValue = parseLeaveDate(leaveSortField === "fromDate" ? (a.fromDate || "") : (a.toDate || ""))
          bValue = parseLeaveDate(leaveSortField === "fromDate" ? (b.fromDate || "") : (b.toDate || ""))
          break
        case "totalDays":
          aValue = a.totalDays ?? 0
          bValue = b.totalDays ?? 0
          break
        default:
          return 0
      }
      
      if (typeof aValue === "number" && typeof bValue === "number") {
        return leaveSortDirection === "asc" ? aValue - bValue : bValue - aValue
      }
      
      if (leaveSortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })
  }

  const timesheetTotalPages = Math.ceil(filteredTimesheetData.length / (timesheetPageSize))
  const timesheetStartIndex = (timesheetCurrentPage - 1) * (timesheetPageSize)
  const timesheetEndIndex = timesheetStartIndex + (timesheetPageSize)
  const currentTimesheetData = filteredTimesheetData.slice(timesheetStartIndex, timesheetEndIndex)
  const leaveTotalPages = Math.ceil(filteredLeaveData.length / (leavePageSize))
  const leaveStartIndex = (leaveCurrentPage - 1) * (leavePageSize)
  const leaveEndIndex = leaveStartIndex + (leavePageSize)
  const currentLeaveData = filteredLeaveData.slice(leaveStartIndex, leaveEndIndex)

  const pendingTimesheets = currentTimesheetData.filter((ts) => ts.tsstatus === "pending")
  const selectedTimesheetData = currentTimesheetData.filter((ts) => selectedTimesheets.includes(ts.id))
  const isAllSelected =
    pendingTimesheets.length > 0 && pendingTimesheets.every((ts) => selectedTimesheets.includes(ts.id))
  const isIndeterminate = selectedTimesheets.length > 0 && !isAllSelected

  return (
    <div className={`p-4 sm:p-6 space-y-6 text-gray-900 bg-gray-50`}>
      <Card className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <CardHeader className="border-b border-gray-200 bg-white p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Hourglass className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            Review & Approvals
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs defaultValue="timesheet" className="w-full">
            <TabsList className="grid w-full grid-cols-2 rounded-none bg-gray-50 h-10 sm:h-12 text-sm sm:text-base">
              <TabsTrigger
                value="timesheet"
                className="truncate px-2 sm:px-4 border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-white data-[state=active]:text-blue-900 text-gray-600 font-medium"
              >
                Timesheet Approvals
              </TabsTrigger>
              <TabsTrigger
                value="leave"
                className="truncate px-2 sm:px-4 border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-white data-[state=active]:text-lightblue-900 text-gray-600 font-medium"
              >
                Leave Request Approvals
              </TabsTrigger>
            </TabsList>

            {/* Timesheet Tab */}
            <TabsContent value="timesheet" className="mt-4 sm:mt-6">
              {/* Search Bar */}
              <div className="mb-4 mx-2 sm:mx-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search by name, duration, time, or submitted date..."
                    value={timesheetSearchTerm}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setTimesheetSearchTerm(e.target.value)
                      setTimesheetCurrentPage(1)
                    }}
                    className="pl-10"
                  />
                </div>
                {timesheetSearchTerm && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setTimesheetSearchTerm("")
                        setTimesheetCurrentPage(1)
                      }}
                      className="whitespace-nowrap"
                    >
                      Clear Search
                    </Button>
                  </div>
                )}
              </div>

              {/* Loading State */}
              {timesheetLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-500">Loading timesheets...</div>
                </div>
              )}

              {/* Error State */}
              {timesheetError && !timesheetLoading && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mx-2 sm:mx-6 mb-4">
                  <div className="text-red-700">{timesheetError}</div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={fetchPendingTimesheets}
                    className="mt-2 text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Retry
                  </Button>
                </div>
              )}

              {/* Bulk Actions Bar */}
              {false && selectedTimesheets.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mx-2 sm:mx-6 mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <span className="text-sm font-medium text-blue-700">
                      {selectedTimesheets.length} timesheet{selectedTimesheets.length > 1 ? "s" : ""} selected
                    </span>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedTimesheets([])}
                        className="text-gray-600 border-gray-300 hover:bg-gray-50"
                      >
                        Clear Selection
                      </Button>
                      <Button size="sm" disabled className="bg-gray-300 text-gray-600">Bulk Actions</Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty State for timesheets */}
              {!timesheetLoading && !timesheetError && filteredTimesheetData.length === 0 && (
                <div className="mx-2 sm:mx-6 mb-4">
                  <div className="border border-gray-200 rounded-lg bg-white p-6 text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {apiTimesheetData.length === 0 
                        ? "No Pending Approvals" 
                        : "No results found"}
                    </h3>
                    <p className="mt-1 text-gray-600">
                      {apiTimesheetData.length === 0
                        ? "You don't have any timesheets pending for approval"
                        : "Try adjusting your search terms"}
                    </p>
                  </div>
                </div>
              )}

              {/* Table - only show when not loading and no error */}
              {!timesheetLoading && !timesheetError && filteredTimesheetData.length > 0 && (
                <div className="overflow-x-auto">
                  <Table className="min-w-[750px] text-sm sm:text-base">
                    <TableHeader>
                      <TableRow className="bg-gray-50 border-b border-gray-200">
                        <TableHead className="w-12 font-semibold text-gray-700 p-2 sm:p-4">
                          <Checkbox
                            disabled={true}
                            checked={isAllSelected}
                            onCheckedChange={handleSelectAll}
                            className="border-gray-300"
                            ref={(el) => {
                              if (el) (el as unknown as HTMLInputElement).indeterminate = isIndeterminate
                            }}
                          />
                        </TableHead>
                        <TableHead className="w-12 font-semibold text-gray-700 p-2 sm:p-4">Sr.No.</TableHead>
                        <TableHead className="font-semibold text-gray-700 p-2 sm:p-4">
                          <Button
                            variant="ghost"
                            className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                            onClick={() => handleSort("name")}
                          >
                            Name
                            {getSortIcon("name")}
                          </Button>
                        </TableHead>
                        <TableHead className="font-semibold text-gray-700 p-2 sm:p-4">
                          <Button
                            variant="ghost"
                            className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                            onClick={() => handleSort("duration")}
                          >
                            Duration
                            {getSortIcon("duration")}
                          </Button>
                        </TableHead>
                        <TableHead className="font-semibold text-gray-700 p-2 sm:p-4">Time</TableHead>
                        <TableHead className="font-semibold text-gray-700 p-2 sm:p-4">
                          <Button
                            variant="ghost"
                            className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                            onClick={() => handleSort("submittedOn")}
                          >
                            Submitted On
                            {getSortIcon("submittedOn")}
                          </Button>
                        </TableHead>
                        <TableHead className="font-semibold text-gray-700 p-2 sm:p-4">Comments</TableHead>
                        <TableHead className="w-32 font-semibold text-gray-700 p-2 sm:p-4">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentTimesheetData.map((timesheet, index) => (
                        <TableRow
                          key={timesheet.id}
                          className="hover:bg-gray-50 border-b border-gray-100"
                        >
                          <TableCell className="p-2 sm:p-4">
                            <Checkbox
                              disabled={true}
                              checked={selectedTimesheets.includes(timesheet.id)}
                              onCheckedChange={(checked) => handleTimesheetSelect(timesheet.id, checked as boolean)}
                              //disabled={timesheet.tsstatus !== "pending"}
                              className="border-gray-300"
                            />
                          </TableCell>
                          <TableCell className="p-2 sm:p-4 text-center">
                            <div
                              className={`mx-auto w-6 sm:w-8 h-6 sm:h-8 rounded-full flex items-center justify-center font-bold border-2 ${timesheet.tsstatus === "approved"
                                ? "border-green-300 bg-green-100 text-black"
                                : timesheet.tsstatus === "rejected"
                                  ? "border-red-300 bg-red-100 text-black"
                                  : "border-transparent text-black bg-transparent"
                                }`}
                            >
                              {timesheetStartIndex + index + 1}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium text-gray-900 p-2 sm:p-4">{timesheet.name}</TableCell>
                          <TableCell className="text-gray-700 p-2 sm:p-4">{timesheet.duration}</TableCell>
                          <TableCell className="text-gray-700 p-2 sm:p-4">{timesheet.time}</TableCell>
                          <TableCell className="text-gray-700 p-2 sm:p-4">{timesheet.submittedOn}</TableCell>
                          <TableCell className="p-2 sm:p-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewComments(timesheet)}
                              className="h-8 px-2 sm:px-3 text-gray-700 hover:bg-gray-100"
                            >
                              {timesheet.comments} Comments
                            </Button>
                          </TableCell>
                          <TableCell className="p-2 sm:p-4">
                            <Button
                              size="sm"
                              onClick={() => handleTimesheetReview(timesheet)}
                              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1 sm:gap-2 font-medium"
                            >
                              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <Pagination
                currentPage={timesheetCurrentPage}
                totalPages={timesheetTotalPages}
                pageSize={timesheetPageSize}
                totalItems={filteredTimesheetData.length}
                onPageChange={setTimesheetCurrentPage}
                onPageSizeChange={(size) => { setTimesheetPageSize(size); setTimesheetCurrentPage(1); }}
              />
            </TabsContent>

            {/* Leave Tab */}
            <TabsContent value="leave" className="mt-4 sm:mt-6">
              {/* Search Bar */}
              <div className="mb-4 mx-2 sm:mx-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search by employee name, leave type, or dates..."
                    value={leaveSearchTerm}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setLeaveSearchTerm(e.target.value)
                      setLeaveCurrentPage(1)
                    }}
                    className="pl-10"
                  />
                </div>
                {leaveSearchTerm && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setLeaveSearchTerm("")
                        setLeaveCurrentPage(1)
                      }}
                      className="whitespace-nowrap"
                    >
                      Clear Search
                    </Button>
                  </div>
                )}
              </div>

              {/* Loading State */}
              {leaveLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-500">Loading leave approvals...</div>
                </div>
              )}

              {/* Error State */}
              {leaveError && !leaveLoading && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mx-2 sm:mx-6 mb-4">
                  <div className="text-red-700">{leaveError}</div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={fetchManagerApprovals}
                    className="mt-2 text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Retry
                  </Button>
                </div>
              )}

              {/* Empty State */}
              {!leaveLoading && !leaveError && filteredLeaveData.length === 0 && (
                <div className="mx-2 sm:mx-6 mb-4">
                  <div className="border border-gray-200 rounded-lg bg-white p-6 text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {apiLeaveData.length === 0 
                        ? "No Leave Approvals" 
                        : "No results found"}
                    </h3>
                    <p className="mt-1 text-gray-600">
                      {apiLeaveData.length === 0
                        ? "You don't have any leave requests to review"
                        : "Try adjusting your search terms"}
                    </p>
                  </div>
                </div>
              )}

              {!leaveLoading && !leaveError && filteredLeaveData.length > 0 && (
                <div className="overflow-x-auto">
                  <Table className="min-w-[700px] text-sm sm:text-base">
                    <TableHeader>
                      <TableRow className="bg-gray-50 border-b border-gray-200">
                        <TableHead className="w-12 font-semibold text-gray-700 p-2 sm:p-4">Sr.No.</TableHead>
                        <TableHead className="font-semibold text-gray-700 p-2 sm:p-4">
                          <Button
                            variant="ghost"
                            className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                            onClick={() => handleLeaveSort("requestBy")}
                          >
                            Request By
                            {getLeaveSortIcon("requestBy")}
                          </Button>
                        </TableHead>
                        <TableHead className="font-semibold text-gray-700 p-2 sm:p-4">
                          <Button
                            variant="ghost"
                            className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                            onClick={() => handleLeaveSort("leaveType")}
                          >
                            Type of Leave
                            {getLeaveSortIcon("leaveType")}
                          </Button>
                        </TableHead>
                        <TableHead className="font-semibold text-gray-700 p-2 sm:p-4">
                          <Button
                            variant="ghost"
                            className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                            onClick={() => handleLeaveSort("fromDate")}
                          >
                            From Date
                            {getLeaveSortIcon("fromDate")}
                          </Button>
                        </TableHead>
                        <TableHead className="font-semibold text-gray-700 p-2 sm:p-4">
                          <Button
                            variant="ghost"
                            className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                            onClick={() => handleLeaveSort("toDate")}
                          >
                            To Date
                            {getLeaveSortIcon("toDate")}
                          </Button>
                        </TableHead>
                        <TableHead className="font-semibold text-gray-700 p-2 sm:p-4">
                          <Button
                            variant="ghost"
                            className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                            onClick={() => handleLeaveSort("totalDays")}
                          >
                            Total Days
                            {getLeaveSortIcon("totalDays")}
                          </Button>
                        </TableHead>
                        <TableHead className="w-32 font-semibold text-gray-700 p-2 sm:p-4">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentLeaveData.map((leave, index) => (
                        <TableRow
                          key={leave.id}
                          className={`hover:bg-gray-50 border-b border-gray-100 ${leave.statusLabel === "cancelled" ? "text-gray-400" : ""}`}
                        >
                          <TableCell className="p-2 sm:p-4 text-center">
                            <div
                              className={`mx-auto w-6 sm:w-8 h-6 sm:h-8 rounded-full flex items-center justify-center font-bold border-2 ${
                                leave.statusLabel === "rejected" || leave.rawStatus === 7
                                  ? "border-red-300 bg-red-100 text-black"
                                  : "border-transparent text-black bg-transparent"
                              }`}
                            >
                              {leaveStartIndex + index + 1}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium text-gray-900 p-2 sm:p-4">{leave.requestBy}</TableCell>
                          <TableCell className="text-gray-700 p-2 sm:p-4">{leave.leaveType}</TableCell>
                          <TableCell className="text-gray-700 p-2 sm:p-4">{leave.fromDate}</TableCell>
                          <TableCell className="text-gray-700 p-2 sm:p-4">{leave.toDate}</TableCell>
                          <TableCell className="text-gray-700 p-2 sm:p-4">{leave.totalDays}</TableCell>
                          <TableCell className="p-2 sm:p-4">
                            <Button
                              size="sm"
                              onClick={() => handleLeaveReview(leave)}
                              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1 sm:gap-2 font-medium"
                            >
                              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <Pagination
                currentPage={leaveCurrentPage}
                totalPages={leaveTotalPages}
                pageSize={leavePageSize}
                totalItems={filteredLeaveData.length}
                onPageChange={setLeaveCurrentPage}
                onPageSizeChange={(size) => { setLeavePageSize(size); setLeaveCurrentPage(1); }}
              />
            </TabsContent>
          </Tabs>
        </CardContent>

        {/* Modals */}
        <TimesheetReviewModal
          isOpen={isTimesheetModalOpen}
          onClose={() => setIsTimesheetModalOpen(false)}
          timesheet={selectedTimesheet}
          isViewOnly={false}
          onActionComplete={(result, id) => {
            setApiTimesheetData(prev => {
              if (result === "approved") {
                return prev.map(ts => ts.id === id ? { ...ts, tsstatus: "approved" } : ts)
              }
              // rejected: keep in list but mark as rejected
              return prev.map(ts => ts.id === id ? { ...ts, tsstatus: "rejected" } : ts)
            })
            // also clear selection if needed
            setSelectedTimesheets(prev => prev.filter(tsId => tsId !== id))
          }}
        />

        <LeaveReviewModal
          isOpen={isLeaveModalOpen}
          onClose={() => setIsLeaveModalOpen(false)}
          leaveRequest={selectedLeave}
          onActionComplete={(result, requestID, comment) => {
            if (result === "approved") {
              setApiLeaveData(prev => prev.filter(l => Number(l.requestID) !== Number(requestID)))
            } else {
              setApiLeaveData(prev => {
                const targetId = Number(requestID)
                return prev.map(l => Number(l.requestID) === targetId ? { ...l, statusLabel: "rejected", comments: comment || l.comments, rawStatus: 7 } : l)
              })
            }
            fetchManagerApprovals()
          }}
        />
  
        <BulkApprovalModal
          isOpen={isBulkApprovalModalOpen}
          onClose={() => setIsBulkApprovalModalOpen(false)}
          selectedTimesheets={selectedTimesheetData}
          onBulkApprove={handleBulkApprove}
          onBulkReject={handleBulkReject}
        />

        <CommentsModal
          isOpen={isCommentsModalOpen}
          onClose={() => setIsCommentsModalOpen(false)}
          comments={selectedComments}
          title={selectedCommentsTitle}
        />
      </Card>
    </div>
  )
}